<script setup lang="ts">
import { mdiArrowRightBoldBoxOutline, mdiArrowLeftBoldBoxOutline } from '@mdi/js'
// #738: 木板の額縁背景は `<img> + object-fit: cover` だとコンテナのアスペクト比
// （mobile 360/500 = 0.72）と画像の自然比（800/558 ≒ 1.434）が食い違って
// 左右が大きくクロップされ、額縁の縁が消えて上部のタブまで隠れる。
// 装飾画像なので CSS background-image + `background-size: 100% 100%`（旧 fill 相当）で
// 4 隅をコンテナに合わせて描画する。Lighthouse `image-aspect-ratio` は `<img>` にしか
// 適用されないので、cover に強制されない（元の a11y 対策 ef7d48d4 を後退させない）。
import galleryBg from '~/assets/images/GalleryBackground.webp'

import type { WorksSortKey } from '~/composables/api/useWorkApi'
import type { WorkGalleryTab } from '~/composables/useWorkGallery'

interface Props {
  /** #737: / のあそぶセクションのサブコピー。未指定なら旧 / の文言（現在は未使用の互換）。 */
  subtitle?: string
}
const props = defineProps<Props>()

// #738: <img fetchpriority="high"> を廃止した分の LCP 影響を preload で補償する。
useHead({
  link: [
    { rel: 'preload', as: 'image', href: galleryBg, fetchpriority: 'high' },
  ],
})

// トップは「お気に入り」を出さない（未ログイン訪問者が主対象のため）。
const categories: WorkGalleryTab[] = [
  { key: 'new', label: '新着' },
  { key: 'featured', label: '注目' },
  { key: 'popular', label: '人気' },
]

const activeCategory = ref<WorksSortKey>('new')

const api = useApi()

// #803: 取得とタブ切替は WorkGallery（+ useWorkGallery）が持つ。トップは
// ショーケースなので `mode="fixed"` の 6 件打ち止め＝追加ロードはしない。
const TOP_WORKS_COUNT = 6

const fetchWorks = async (page: number, perPage: number, sort: WorksSortKey) => {
  const resp = await api.listPublicWorks(page, perPage, sort)
  return { works: resp.works, total: resp.total }
}

const workItemIndex = ref<number>(0)
const worksDescSwitch = ref<number>(0)
const worksDescIntervalId = ref<NodeJS.Timeout | null>(null)

const startAutoSwitch = () => {
  worksDescIntervalId.value = setInterval(switchTab, 5000)
}

const stopAutoSwitch = () => {
  if (worksDescIntervalId.value) {
    clearInterval(worksDescIntervalId.value)
    worksDescIntervalId.value = null
  }
}

const switchTab = () => {
  worksDescSwitch.value = worksDescSwitch.value ^ 1
}

onMounted(() => {
  startAutoSwitch()
})

onBeforeUnmount(() => {
  stopAutoSwitch()
})
</script>

<template>
  <div class="centered-area">
    <!-- Big title with fade animation -->
    <LandingFadeBigTitle>
      <template #main>
        あそぶ
      </template>
      <template #sub>
        <template v-if="props.subtitle">
          <span class="sub-multiline">{{ props.subtitle }}</span>
        </template>
        <template v-else>
          <div class="d-sm-none">
            Pyxel だから、ミニゲームから本格的なものまでつくれちゃう。必要なものはブラウザだけ！
          </div>
          <div class="d-none d-sm-block">
            Pyxel だから、ミニゲームから本格的なものまでつくれちゃう。<br>必要なものはブラウザだけ！
          </div>
        </template>
      </template>
    </LandingFadeBigTitle>

    <!-- Gallery -->
    <!-- #738: 背景は CSS background-image（旧 fill 相当）で 4 隅をコンテナに合わせる。
         <img> + object-fit だとモバイルで cover でも fill でも破綻するため装飾に降格。 -->
    <div
      id="gallary"
      :style="{ '--gallery-bg': `url(${galleryBg})` }"
    >
      <!-- Gallery Content -->
      <!-- #803: 取得・タブ切替は WorkGallery が持つ。トップはショーケースなので
           `mode="fixed"` の 6 件打ち止め（スクロール追加ロードはしない）。タブは
           モバイル / デスクトップそれぞれの器の中に置くため、`tabs` prop は使わず
           `WorkGalleryTabs` を直接並べる。 -->
      <WorkGallery
        class="gallary-overlay"
        :sort="activeCategory"
        :fetcher="fetchWorks"
        :per-page="TOP_WORKS_COUNT"
        mode="fixed"
        @update:sort="activeCategory = $event"
      >
        <template #loading>
          <div class="gallary-content">
            <v-row
              align-content="center"
              class="fill-height"
              justify="center"
            >
              <v-col
                class="text-subtitle-1 text-center"
                cols="12"
              >
                <span class="font-weight-bold text-indigo-darken-4">楽しいゲームを読み込み中 ...</span>
              </v-col>
              <v-col cols="6">
                <v-progress-linear
                  indeterminate
                  color="indigo-darken-4"
                  height="10"
                  rounded
                />
              </v-col>
            </v-row>
          </div>
        </template>

        <template #default="{ works }">
          <div class="gallary-content">
            <!-- Mobile: horizontal tab carousel -->
            <div class="d-sm-none">
              <!-- Category tabs (mobile) -->
              <WorkGalleryTabs
                class="gallary-category-mobile"
                :tabs="categories"
                :model-value="activeCategory"
                @update:model-value="activeCategory = $event"
              />

              <v-tabs
                v-model="workItemIndex"
                bg-color="#263238ff"
                :next-icon="mdiArrowRightBoldBoxOutline"
                :prev-icon="mdiArrowLeftBoldBoxOutline"
                show-arrows
                center-active
              >
                <v-tab
                  v-for="(item, i) in works"
                  :key="item.workId"
                  :text="item.title"
                  :value="i"
                />
              </v-tabs>
              <v-tabs-window v-model="workItemIndex">
                <v-tabs-window-item
                  v-for="(item, i) in works"
                  :key="item.workId"
                  :value="i"
                >
                  <WorkCard
                    :work="item"
                    :interactive="false"
                    @play="navigateTo(item.publicUrl || '')"
                  />
                </v-tabs-window-item>
              </v-tabs-window>
            </div>

            <!-- Desktop: 3-column grid -->
            <div class="d-none d-sm-block">
              <!-- Category tabs (desktop) -->
              <WorkGalleryTabs
                class="gallary-category"
                :tabs="categories"
                :model-value="activeCategory"
                @update:model-value="activeCategory = $event"
              />

              <v-container fluid>
                <v-row>
                  <!-- #805: key は **作品 ID**。添字にすると、タブ切替で中身が総
                       取っ替えになってもインスタンスが再利用され、`WorkCard` の
                       ローカル状態（いいね数・♥）が前の作品のまま残る。 -->
                  <v-col
                    v-for="item in works"
                    :key="item.workId"
                    cols="12"
                    sm="4"
                    class="pa-1"
                  >
                    <WorkCard
                      :work="item"
                      :interactive="false"
                      @play="navigateTo(item.publicUrl || '')"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </div>
        </template>
      </WorkGallery>
    </div>

    <!-- #843: ここは `mode="fixed"` の 6 件打ち止めなので、続きは公開ギャラリーへ送る。
         これが無いと、トップに来た未認証の訪問者は 6 件で行き止まりになる
         （`/arcanelab` は認証必須で、サイト内に他の一覧への導線が無かった）。 -->
    <p class="more-works">
      <NuxtLink
        class="more-works__link"
        to="/works"
      >
        もっと遊びたい人は、作品をぜんぶ見る →
      </NuxtLink>
    </p>
  </div>
</template>

<style scoped>
.centered-area {
  width: 100%;
  height: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
}

/* #737: subtitle prop 内の改行(\n)を反映する */
.sub-multiline {
  white-space: pre-line;
}

/* #738: 装飾フレーム画像は CSS background で描画する（旧 fill 相当）。
   `background-size: 100% 100%` にすることで、コンテナ 4 隅と画像 4 隅を
   ピタリと揃える（額縁の縁がモバイルでもデスクトップでも見切れない）。
   Lighthouse `image-aspect-ratio` は <img> にのみ適用されるためこの経路では回避できる。 */
#gallary {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-image: var(--gallery-bg);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
}

/* #738: モバイルは中身（カテゴリタブ + v-tabs + WorkCard[画像 aspect 1:1]）が
   合計 550–600px 前後になり、固定 aspect-ratio 360/500 (=500px) に入りきらず
   `overflow: hidden` + `align-items: center` で上下均等にクリップされていた
   （タブが欄外・カード下端が見切れ）。コンテナに固定比を持たせず、内容の
   高さがそのままコンテナ高さになるようにする。木枠は `background-size: 100% 100%`
   なのでコンテナ高さに合わせて縦に伸縮する（4 隅は常にピタリ）。 */
@media (max-width: 600px) {
  #gallary {
    /* aspect-ratio は付けない。内容が伸びたらコンテナも伸びる。 */
    min-height: 360px;
  }
}

@media (min-width: 600.02px) {
  #gallary {
    aspect-ratio: 1180 / 1120;
  }
}

.gallary-overlay {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* デスクトップだけ「aspect 固定コンテナに絶対配置で中央寄せ」パターンを使う。
   モバイルは静的配置で content-driven（上のコメント参照）。 */
@media (min-width: 600.02px) {
  .gallary-overlay {
    position: absolute;
    inset: 0;
    height: 100%;
  }
}

.gallary-content {
  width: 100%;
  height: auto;

  z-index: 3;
  padding: clamp(18.89px, 2.66dvw, 34px);
  padding-top: clamp(12.22px, 1.72dvw, 22px);
  box-sizing: border-box;
}

/* #738: モバイルは content-driven になった分、木枠のドット装飾線と
   カテゴリタブが近すぎて詰まって見える。上下に少し余白を足す。 */
@media (max-width: 600px) {
  .gallary-content {
    padding-top: clamp(24px, 5.5dvw, 34px);
    padding-bottom: clamp(24px, 5.5dvw, 34px);
  }
}

/* Category tabs */
/* #803: タブの見た目は WorkGalleryTabs（`pill` variant）が持つ。ここは landing
   なのでフォントサイズも既定（dvw ベース）のままでよい。 */

/* #843: 額縁の外、下端中央に置く「続きを見る」導線。 */
.more-works {
  margin: 12px 0 0;
  text-align: center;
}

.more-works__link {
  display: inline-block;
  padding: 6px 18px;
  border: 1px solid var(--landing-color-navy);
  border-radius: var(--landing-radius);
  color: var(--landing-color-navy);
  background: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: clamp(13px, 1.4dvw, 16px);
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: background 0.15s, color 0.15s;
}

.more-works__link:hover {
  background: var(--landing-color-navy);
  color: var(--landing-color-parchment);
}
</style>
